import { useEffect, useState, useMemo, type CSSProperties, type FormEvent } from "react";
import {
  listPartners,
  savePartner,
  deletePartner,
  type Partner,
} from "../../lib/api";
import PublishingStudioTabs from "../../components/admin/PublishingStudioTabs";
import { useIsMobile } from "../../lib/useIsMobile";

const PARTNER_TYPES = [
  { id: "all", label: "All Types" },
  { id: "brand", label: "Distillery & Brand", badgeColor: "#F9AF36", badgeBg: "rgba(249,175,54,0.15)" },
  { id: "charity", label: "Charity & Non-Profit", badgeColor: "#27ae60", badgeBg: "rgba(39,174,96,0.15)" },
  { id: "organization", label: "Civic Organization", badgeColor: "#4A8FD9", badgeBg: "rgba(74,143,217,0.15)" },
  { id: "media", label: "Media & Press", badgeColor: "#9B59B6", badgeBg: "rgba(155,89,182,0.15)" },
  { id: "distributor", label: "Distributor", badgeColor: "var(--muted)", badgeBg: "rgba(138,122,102,0.15)" },
  { id: "individual", label: "Individual Contributor", badgeColor: "var(--muted)", badgeBg: "rgba(138,122,102,0.15)" },
];

function slugify(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export default function AdminPartners() {
  // Card text fixes apply below the 1024 tablet only (L121). Breaking long names mid-word beats
  // running off a phone; on wide cards it split ordinary words ("Commonwe/alth") that master
  // kept whole, so 1024+ keeps master's card styles exactly.
  const isNarrow = useIsMobile(1024);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("active");
  const [searchQuery, setSearchQuery] = useState("");

  // Drawer / Modal State
  const [editingPartner, setEditingPartner] = useState<Partial<Partner> | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Toast
  const [toast, setToast] = useState<{ text: string; type: "ok" | "err" } | null>(null);
  const showToast = (text: string, type: "ok" | "err" = "ok") => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Escape key closes modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isFormOpen) setIsFormOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFormOpen]);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await listPartners();
      setPartners(data || []);
    } catch (err: any) {
      showToast(err.message || "Failed to load partners", "err");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredPartners = useMemo(() => {
    let list = partners;
    if (typeFilter !== "all") {
      list = list.filter((p) => (p.partner_type || "").toLowerCase() === typeFilter);
    }
    if (statusFilter === "active") {
      list = list.filter((p) => p.active !== false);
    } else if (statusFilter === "inactive") {
      list = list.filter((p) => p.active === false);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          (p.description && p.description.toLowerCase().includes(q)) ||
          (p.website_url && p.website_url.toLowerCase().includes(q))
      );
    }
    return list;
  }, [partners, typeFilter, statusFilter, searchQuery]);

  const handleNewPartner = () => {
    setEditingPartner({
      name: "",
      slug: "",
      partner_type: "brand",
      description: "",
      website_url: "",
      logo_url: "",
      facebook_handle: "",
      facebook_page_id: "",
      instagram_handle: "",
      active: true,
    });
    setIsFormOpen(true);
  };

  const handleEditPartner = (p: Partner) => {
    setEditingPartner({ ...p });
    setIsFormOpen(true);
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingPartner || !editingPartner.name) return;
    setSaving(true);
    try {
      const payload: Partial<Partner> = {
        ...editingPartner,
        slug: editingPartner.slug || slugify(editingPartner.name),
        facebook_handle: (editingPartner.facebook_handle || "").replace(/^@/, "").trim() || null,
        instagram_handle: (editingPartner.instagram_handle || "").replace(/^@/, "").trim() || null,
        website_url: editingPartner.website_url ? editingPartner.website_url.trim() : null,
        logo_url: editingPartner.logo_url ? editingPartner.logo_url.trim() : null,
      };
      await savePartner(payload);
      showToast(`✓ Partner "${payload.name}" saved!`);
      setIsFormOpen(false);
      await loadData();
    } catch (err: any) {
      showToast(err.message || "Failed to save partner", "err");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to remove partner "${name}"?`)) return;
    try {
      await deletePartner(id);
      showToast(`✓ Removed "${name}"`);
      setIsFormOpen(false);
      await loadData();
    } catch (err: any) {
      showToast(err.message || "Failed to remove partner", "err");
    }
  };

  return (
    <div style={styles.container}>
      <PublishingStudioTabs activeTab="partners" badgeCounts={{ partners: partners.length }} />
      {toast && (
        <div style={{ ...styles.toast, background: toast.type === "err" ? "#E05040" : "var(--gold)" }}>
          {toast.text}
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Brand Partners &amp; Community</h1>
          <p style={styles.subtitle}>
            Manage visiting distilleries, charity recipients, civic organizations, and co-branded event sponsors.
          </p>
        </div>
        <button type="button" style={styles.btnPrimary} onClick={handleNewPartner}>
          + Add Partner
        </button>
      </div>

      {/* Toolbar */}
      <div style={styles.toolbar}>
        <input
          type="text"
          placeholder="Search partners by name, website, description…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchInput}
        />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={styles.select}
          >
            {PARTNER_TYPES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            style={styles.select}
          >
            <option value="active">Active Only</option>
            <option value="all">All Statuses</option>
            <option value="inactive">Inactive Only</option>
          </select>
          <span style={styles.partnerCount}>
            {filteredPartners.length} {filteredPartners.length === 1 ? "partner" : "partners"}
          </span>
        </div>
      </div>

      {/* Partners List */}
      {loading ? (
        <div style={styles.loader}>Loading partners…</div>
      ) : filteredPartners.length === 0 ? (
        <div style={styles.emptyState}>No partners found matching the selected criteria.</div>
      ) : (
        <div style={styles.partnersGrid}>
          {filteredPartners.map((p) => {
            const pType = PARTNER_TYPES.find((t) => t.id === p.partner_type) || PARTNER_TYPES[1];
            return (
              <div
                key={p.id}
                style={isNarrow ? { ...styles.partnerCard, ...narrowCard } : styles.partnerCard}
                onClick={() => handleEditPartner(p)}
              >
                <div style={isNarrow ? { ...styles.cardTop, ...narrowCardTop } : styles.cardTop}>
                  {p.logo_url ? (
                    <img src={p.logo_url} alt="" style={styles.logoImg} loading="lazy" />
                  ) : (
                    <div style={styles.avatarPlaceholder}>
                      {p.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div style={{ flex: isNarrow ? "1 1 calc(100% - 56px)" : 1, minWidth: 0 }}>
                    <div style={isNarrow ? { ...nameRow, ...narrowNameRow } : nameRow}>
                      <h3 style={isNarrow ? { ...styles.cardName, ...narrowText } : styles.cardName}>{p.name}</h3>
                      {!p.active && <span style={isNarrow ? { ...styles.inactiveTag, ...narrowBadge } : styles.inactiveTag}>Inactive</span>}
                    </div>
                    {p.website_url && (
                      <a
                        href={p.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={isNarrow ? { ...styles.webLink, ...narrowText, maxWidth: "100%" } : styles.webLink}
                      >
                        {p.website_url.replace(/^https?:\/\//, "")}
                      </a>
                    )}
                  </div>
                  <span
                    style={{
                      ...styles.typeBadge,
                      ...(isNarrow ? narrowTypeBadge : null),
                      background: pType.badgeBg,
                      color: pType.badgeColor,
                    }}
                  >
                    {pType.label.split(" ")[0]}
                  </span>
                </div>

                {p.description && <p style={styles.cardDesc}>{p.description}</p>}

                <div style={isNarrow ? { ...styles.socialRow, overflowWrap: "anywhere" } : styles.socialRow}>
                  {p.facebook_handle && (
                    <span style={styles.fbPill}>FB @{p.facebook_handle}</span>
                  )}
                  {p.instagram_handle && (
                    <span style={styles.igPill}>IG @{p.instagram_handle}</span>
                  )}
                </div>

                <div style={isNarrow ? { ...styles.cardFooter, ...narrowFooter } : styles.cardFooter}>
                  <span style={isNarrow ? narrowSlug : undefined}>Slug: <code>{p.slug}</code></span>
                  <span style={{ color: "var(--gold)", fontWeight: 700, ...(isNarrow ? narrowEditLink : null) }}>Edit Details →</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Editor Modal */}
      {isFormOpen && editingPartner && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {editingPartner.id ? `Edit: ${editingPartner.name}` : "Add New Partner"}
              </h2>
              <button
                type="button"
                style={styles.btnIcon}
                onClick={() => setIsFormOpen(false)}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSave} style={styles.modalForm}>
              <div className="station-form-grid" style={styles.formGrid}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Partner Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. J. Rieger & Co."
                    value={editingPartner.name || ""}
                    onChange={(e) => {
                      const name = e.target.value;
                      setEditingPartner({
                        ...editingPartner,
                        name,
                        slug: editingPartner.slug || slugify(name),
                      });
                    }}
                    style={styles.input}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Slug *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. j-rieger-co"
                    value={editingPartner.slug || ""}
                    onChange={(e) =>
                      setEditingPartner({ ...editingPartner, slug: e.target.value })
                    }
                    style={styles.input}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Partner Type</label>
                  <select
                    value={editingPartner.partner_type || "brand"}
                    onChange={(e) =>
                      setEditingPartner({ ...editingPartner, partner_type: e.target.value })
                    }
                    style={styles.select}
                  >
                    <option value="brand">Distillery &amp; Brand Partner</option>
                    <option value="charity">Charity &amp; Non-Profit</option>
                    <option value="organization">Civic Organization</option>
                    <option value="media">Media &amp; Press</option>
                    <option value="distributor">Distributor</option>
                    <option value="individual">Individual Contributor</option>
                  </select>
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Active Status</label>
                  <select
                    value={editingPartner.active ? "true" : "false"}
                    onChange={(e) =>
                      setEditingPartner({
                        ...editingPartner,
                        active: e.target.value === "true",
                      })
                    }
                    style={styles.select}
                  >
                    <option value="true">Active (Visible in partners &amp; events)</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>

                <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                  <label style={styles.label}>Website URL</label>
                  <input
                    type="url"
                    placeholder="https://jriegerco.com"
                    value={editingPartner.website_url || ""}
                    onChange={(e) =>
                      setEditingPartner({ ...editingPartner, website_url: e.target.value })
                    }
                    style={styles.input}
                  />
                </div>

                <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                  <label style={styles.label}>Logo Image URL</label>
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <input
                      type="url"
                      placeholder="https://..."
                      value={editingPartner.logo_url || ""}
                      onChange={(e) =>
                        setEditingPartner({ ...editingPartner, logo_url: e.target.value })
                      }
                      style={{ ...styles.input, flex: 1 }}
                    />
                    {editingPartner.logo_url && (
                      <img
                        src={editingPartner.logo_url}
                        alt="Logo preview"
                        style={styles.logoPreview}
                      />
                    )}
                  </div>
                </div>

                <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                  <label style={styles.label}>Description &amp; Bio</label>
                  <textarea
                    rows={3}
                    placeholder="Brief background on the brand or partner..."
                    value={editingPartner.description || ""}
                    onChange={(e) =>
                      setEditingPartner({ ...editingPartner, description: e.target.value })
                    }
                    style={styles.textarea}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Instagram Handle</label>
                  <input
                    type="text"
                    placeholder="@jriegerco"
                    value={editingPartner.instagram_handle || ""}
                    onChange={(e) =>
                      setEditingPartner({ ...editingPartner, instagram_handle: e.target.value })
                    }
                    style={styles.input}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Facebook Handle</label>
                  <input
                    type="text"
                    placeholder="@jriegerco"
                    value={editingPartner.facebook_handle || ""}
                    onChange={(e) =>
                      setEditingPartner({ ...editingPartner, facebook_handle: e.target.value })
                    }
                    style={styles.input}
                  />
                </div>
              </div>

              {/* Modal Footer */}
              <div style={styles.modalActions}>
                {editingPartner.id && (
                  <button
                    type="button"
                    style={styles.btnDanger}
                    onClick={() =>
                      handleDelete(editingPartner.id!, editingPartner.name || "Partner")
                    }
                  >
                    Delete Partner
                  </button>
                )}
                <div style={{ display: "flex", gap: 10, marginLeft: "auto" }}>
                  <button
                    type="button"
                    style={styles.btnSecondary}
                    onClick={() => setIsFormOpen(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" style={styles.btnPrimary} disabled={saving}>
                    {saving ? "Saving…" : "Save Partner"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const nameRow: CSSProperties = { display: "flex", alignItems: "center", gap: 6 };
// Below 1024 only (see isNarrow). minWidth 0: a grid item won't shrink below its content, so a
// long link widened the track past a phone screen. The name row wraps, so the Inactive tag drops
// under a name that doesn't fit beside it instead of squeezing it. Names use break-word, NOT
// anywhere: anywhere lets the name shrink below its longest word, which split ordinary names
// ("Kentu/cky") on phones (L122). break-word only splits a single word wider than the whole line.
const narrowCard: CSSProperties = { minWidth: 0 };
// The header wraps and the text column claims the full width beside the avatar (100% − 44px
// avatar − 12px gap), so the type badge always takes its own line, right-aligned. A width
// threshold here only moved the squeeze around: names and domains split at 430 and 820.
const narrowCardTop: CSSProperties = { flexWrap: "wrap", rowGap: 8 };
const narrowTypeBadge: CSSProperties = { marginLeft: "auto" };
// A name with no spaces makes an equally unbroken slug; it wraps inside the footer, and the
// Edit link keeps its line.
const narrowFooter: CSSProperties = { flexWrap: "wrap", gap: 8 };
// Stays right-aligned when a long slug pushes it to the next line.
const narrowEditLink: CSSProperties = { flexShrink: 0, whiteSpace: "nowrap", marginLeft: "auto" };
const narrowSlug: CSSProperties = { minWidth: 0, overflowWrap: "anywhere" };
const narrowNameRow: CSSProperties = { flexWrap: "wrap", rowGap: 2 };
const narrowText: CSSProperties = { minWidth: 0, overflowWrap: "break-word" };
const narrowBadge: CSSProperties = { flexShrink: 0, whiteSpace: "nowrap" };

const styles: Record<string, CSSProperties> = {
  container: { padding: "20px 24px", maxWidth: 1200, margin: "0 auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 700, margin: 0, color: "var(--text)" },
  subtitle: { fontSize: 13, color: "var(--muted)", margin: "4px 0 0" },
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 20 },
  searchInput: { flex: 1, minWidth: 260, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "8px 12px", fontSize: 13 },
  select: { background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "8px 12px", fontSize: 13 },
  partnerCount: { fontSize: 12, color: "var(--muted)", alignSelf: "center", whiteSpace: "nowrap" },
  // min(340px, 100%): on a phone the content box is ~343px, and a long name pushed the 340px card
  // past the screen edge; now one full-width column there, unchanged wherever 340px fits.
  partnersGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(340px, 100%), 1fr))", gap: 16 },
  partnerCard: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 16, cursor: "pointer", display: "flex", flexDirection: "column", gap: 8 },
  cardTop: { display: "flex", alignItems: "flex-start", gap: 12 },
  logoImg: { width: 44, height: 44, borderRadius: 8, objectFit: "contain", background: "rgba(255,255,255,0.04)", border: "1px solid var(--border)" },
  avatarPlaceholder: { width: 44, height: 44, borderRadius: 8, background: "var(--gold)", color: "#111", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 800 },
  cardName: { fontSize: 16, fontWeight: 700, margin: 0, color: "var(--text)" },
  inactiveTag: { fontSize: 10, color: "var(--muted)", textTransform: "uppercase", padding: "1px 4px", border: "1px solid var(--border)", borderRadius: 4 },
  webLink: { fontSize: 12, color: "var(--muted)", textDecoration: "none", marginTop: 2, display: "inline-block" },
  typeBadge: { fontSize: 10, fontWeight: 800, textTransform: "uppercase", padding: "2px 8px", borderRadius: 4, letterSpacing: 0.5 },
  cardDesc: { fontSize: 12, color: "var(--muted)", margin: "4px 0", lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" },
  socialRow: { display: "flex", gap: 6, flexWrap: "wrap", marginTop: 2 },
  fbPill: { fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 4, background: "rgba(24,119,242,0.12)", color: "#5896f8" },
  igPill: { fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 4, background: "rgba(225,48,108,0.12)", color: "#e1306c" },
  cardFooter: { display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid var(--border)", paddingTop: 10, marginTop: 4, fontSize: 11, color: "var(--muted)" },
  // minWidth "min-content": global.css gives phone buttons min-width 44px, which replaces a flex
  // item's content-size floor, so this wrapping label shrank past its own text (4d verifier).
  btnPrimary: { background: "var(--gold)", color: "#111", border: 0, padding: "8px 16px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer", minWidth: "min-content" },
  btnSecondary: { background: "transparent", color: "var(--text)", border: "1px solid var(--border)", padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" },
  btnDanger: { background: "transparent", color: "#E05040", border: "1px solid rgba(224,80,64,0.4)", padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer" },
  btnIcon: { background: "transparent", border: 0, color: "var(--muted)", fontSize: 22, cursor: "pointer" },
  loader: { padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 14 },
  emptyState: { padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 13 },
  toast: { position: "fixed", bottom: 20, right: 20, color: "#111", fontWeight: 700, padding: "10px 18px", borderRadius: 8, zIndex: 9999, fontSize: 13, boxShadow: "0 4px 12px rgba(0,0,0,0.4)" },
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 },
  modalContent: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, width: "100%", maxWidth: 640, maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid var(--border)" },
  modalTitle: { fontSize: 18, fontWeight: 700, color: "var(--text)", margin: 0 },
  modalForm: { padding: 20, overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 16 },
  formGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 },
  fieldGroup: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--muted)", letterSpacing: 0.5 },
  input: { background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "8px 12px", fontSize: 13 },
  textarea: { width: "100%", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "10px 12px", fontSize: 13, resize: "vertical" },
  logoPreview: { width: 38, height: 38, borderRadius: 6, objectFit: "contain", background: "rgba(255,255,255,0.06)", border: "1px solid var(--border)" },
  modalActions: { display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid var(--border)", paddingTop: 16 },
};
